import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { IRegEdIntegrationStatus } from './types';
import { invalidateRegEdIntegration, setRegEdIntegration } from './useRegEdIntegrationQuery';

/**
 * The AdTrax identity a portal filing is submitted under. Admin-set rather than entered by the
 * reviewer: the portal is unauthenticated, so a UNID the officer could type would let anyone holding a
 * link file under an identity of their choosing, and this is the audit field that split protects.
 *
 * An empty value clears it, which is a real configuration — a firm whose advisors hold their own UNIDs
 * has the officer file under the advisor's instead. The API trims and 400s past the 24-character cap.
 */
export const useRegEdOfficerRegEdUserIdMutation = ({
	onError,
	onSuccess,
}: {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: (result: IRegEdIntegrationStatus) => void;
} = {}) => {
	const userSession = useUserSession();
	return useMutation({
		mutationFn: ({
			officerRegEdUserId,
			impersonationContext,
		}: {
			officerRegEdUserId: string;
			impersonationContext?: Api.IImpersonationContext;
		}) =>
			userSession.webServiceHelper.callAsync<IRegEdIntegrationStatus>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					queryParams: { officerRegEdUserId },
					urlPath: 'compliance/regEd/integration/officerRegEdUserId',
				}),
				'PUT',
				null
			),
		onError,
		onSuccess: (result, { impersonationContext }) => {
			setRegEdIntegration(impersonationContext?.account?.id, result);
			invalidateRegEdIntegration();
			onSuccess?.(result);
		},
	});
};
